// @flow
import React, {useState, useMemo, useEffect, useCallback} from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Chip,
  makeStyles,
} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';

import type {StoreProductOption} from '../../../../../entities/product-option';
import type {ProductOptionGroupOptionConfig} from '../../../../../entities/product';
import SearchInput from '../../../../../elements/SearchInput';
import {getImageUrl} from '../../../../../utils/image_provider';

const useStyles = makeStyles(theme => ({
  modalPaper: {
    maxWidth: '600px',
    width: '100%',
  },
  modalHeader: {
    padding: '.75rem',
    borderBottom: `1px solid ${colors.grey200}`,

    [theme.breakpoints.up('md')]: {
      padding: '1.5rem 1.5rem 1rem',
    },
  },
  titleText: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  modalContent: {
    padding: '.5rem',
    minHeight: '400px',
    maxHeight: '500px',
    overflowY: 'auto',

    [theme.breakpoints.up('md')]: {
      padding: '1.5rem',
    },
  },
  searchContainer: {
    marginBottom: '1rem',
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: colors.grey50,
    },
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  optionItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem',
    border: `2px solid ${colors.grey300}`,
    borderRadius: '.75rem',
    backgroundColor: 'white',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: colors.grey200,
    },

    '&[data-disabled="true"]': {
      cursor: 'not-allowed',
      backgroundColor: colors.grey100,
      borderColor: colors.grey200,
      opacity: 0.6,
    },
    '&[data-selected="true"]': {
      borderColor: colors.primary,
    },
  },
  optionInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flex: 1,
  },
  optionThumb: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    border: `2px solid ${colors.grey300}`,
    flexShrink: 0,
  },
  optionImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '.5rem',
  },
  optionDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    flex: 1,
  },
  optionName: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey800,
  },
  optionPrice: {
    fontSize: '0.75rem',
    color: colors.grey600,
  },
  optionStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  inGroupChip: {
    backgroundColor: colors.lightPurple,
    color: colors.primary,
    fontSize: '0.75rem',
    height: '24px',
  },
  selectedCount: {
    fontSize: '0.875rem',
    color: colors.grey600,
    marginBottom: '1rem',
  },
  modalActions: {
    padding: '1rem 1.5rem',
    borderTop: `1px solid ${colors.grey200}`,
    gap: '0.75rem',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem',
    color: colors.grey500,
  },
}));

type Props = {
  open: boolean,
  onClose: () => void,
  onAddOptions: (options: ProductOptionGroupOptionConfig[]) => void,
  availableOptions: StoreProductOption[],
  existingOptionIds: string[],
  groupName: string,
};

function AddOptionsModal({
  open,
  onClose,
  onAddOptions,
  availableOptions,
  existingOptionIds,
  groupName,
}: Props) {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return availableOptions;

    return availableOptions.filter(option =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [availableOptions, searchTerm]);

  const handleOptionToggle = (optionId: string) => {
    if (existingOptionIds.includes(optionId)) return;

    setSelectedOptions(prev =>
      prev.includes(optionId)
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const handleAddSelected = useCallback(async () => {
    if (selectedOptions.length === 0) return;

    setIsLoading(true);

    try {
      const selectedStoreOptions = availableOptions.filter(option =>
        selectedOptions.includes(option.id)
      );

      const mappedOptions: ProductOptionGroupOptionConfig[] = selectedStoreOptions.map(
        (storeOption, index) => ({
          type: 'product-option',
          useDefault: true,
          id: storeOption.id,
          number: storeOption.number || index,
          price: storeOption.price?.toString() || '0',
          enabled: storeOption.enabled,
          storeProductOption: {
            color: storeOption.color,
            enabled: storeOption.enabled,
            id: storeOption.id,
            imageId: storeOption.imageId,
            name: storeOption.name,
            number: storeOption.number,
            price: storeOption.price?.toString() || '0',
            type: 'store-product-option',
          },
        })
      );

      await onAddOptions(mappedOptions);
      setSelectedOptions([]);
      onClose();
    } catch (error) {
      console.error('Error adding options:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedOptions, availableOptions, onAddOptions, onClose]);

  const handleClose = useCallback(() => {
    if (isLoading) return; // Prevent closing while loading
    setSelectedOptions([]);
    setSearchTerm('');
    onClose();
  }, [isLoading, onClose]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;

      if (event.key === 'Escape' && !isLoading) {
        handleClose();
      } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleAddSelected();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, isLoading, selectedOptions.length, handleClose, handleAddSelected]);

  return (
    <CustomModal
      open={open}
      handleCloseModal={handleClose}
      maxWidth="md"
      isSaving={isLoading}
      classes={{paper: classes.modalPaper}}
    >
      <Box className={classes.modalHeader}>
        <Typography className={classes.titleText}>
          Agregar opciones a {groupName}
        </Typography>
      </Box>

      <Box className={classes.modalContent}>
        <Box className={classes.searchContainer}>
          <SearchInput
            fullWidth
            placeholder="Buscar opciones..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </Box>

        <Box className={classes.optionsList}>
          {filteredOptions.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography>No se encontraron opciones</Typography>
            </Box>
          ) : (
            filteredOptions.map(option => {
              const isInGroup = existingOptionIds.includes(option.id);
              const isSelected = selectedOptions.includes(option.id);

              return (
                <Box
                  key={option.id}
                  className={classes.optionItem}
                  component="label"
                  data-disabled={isInGroup}
                  data-selected={isSelected}
                >
                  <Box className={classes.optionInfo}>
                    {option.color && (
                      <Box
                        className={classes.optionThumb}
                        style={{backgroundColor: option.color}}
                      >
                        {option.imageId && (
                          <img
                            className={classes.optionImage}
                            src={getImageUrl(option.imageId)}
                            alt={option.name}
                            style={{width: '100%', height: '100%'}}
                          />
                        )}
                      </Box>
                    )}
                    <Box className={classes.optionDetails}>
                      <Typography className={classes.optionName}>
                        {option.name}
                      </Typography>
                      {option.price && (
                        <Typography className={classes.optionPrice}>
                          ${option.price}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box className={classes.optionStatus}>
                    {isInGroup ? (
                      <Chip
                        label="En grupo"
                        size="small"
                        className={classes.inGroupChip}
                      />
                    ) : (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleOptionToggle(option.id)}
                            color="primary"
                            size="small"
                          />
                        }
                        label=""
                        style={{margin: 0}}
                      />
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      <Box className={classes.modalActions}>
        <CustomButton variant="outlined" onClick={handleClose} color="default">
          Cancelar
        </CustomButton>
        <CustomButton
          variant="contained"
          onClick={handleAddSelected}
          disabled={selectedOptions.length === 0 || isLoading}
          color="primary"
        >
          {isLoading ? 'Agregando...' : 'Agregar'}{' '}
          {!isLoading && selectedOptions.length > 0
            ? `(${selectedOptions.length})`
            : ''}
        </CustomButton>
      </Box>
    </CustomModal>
  );
}

export default AddOptionsModal;
